import {
  parseQuery,
  type LexicalSearchFilter,
} from '@atlassiansox/code-search-query-parser';

import { urls } from 'src/urls/workspaces';
import { authRequest, AuthRequestOptions, jsonHeaders } from 'src/utils/fetch';

import type { SearchResponse } from '../api-types';

import { composeScopedFilter } from './compose-scoped-filter';
import { encodeQueryTerms } from './encode-query-terms';

// The # of files returned per-page
const DEFAULT_PAGE_SIZE = 50;

type FetchSearchOptions = {
  cursor?: string;
  filters?: LexicalSearchFilter;
  pageSize?: number;
  requestId?: string;
  searchTerm: string;
};

export async function fetchSearchResults(
  { cursor, filters, pageSize, requestId, searchTerm }: FetchSearchOptions,
  csrftoken?: string
) {
  const body = {
    query: {
      filters,
      search_term: searchTerm,
    },
    config: {
      max_results: pageSize || DEFAULT_PAGE_SIZE,
      search_mode: 'LEXICAL',
    },
    ...(cursor ? { pagination: { next_cursor: cursor } } : {}),
    ...(requestId ? { request_id: requestId } : {}),
  };

  const options: AuthRequestOptions = {
    method: 'POST',
    headers: {
      ...jsonHeaders,
      'X-RovoDev-Xid': 'bitbucket',
    },
    body: JSON.stringify(body),
  };

  const response = await fetch(
    authRequest(urls.api.internal.codeSearch, options, csrftoken)
  );

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }

  return (await response.json()) as SearchResponse;
}

type FetchWorkspaceSearchOptions = {
  cursor?: string;
  requestId?: string;
  workspaceSlug: string;
  searchQuery: string;
};

export function fetchWorkspaceSearchResults(
  {
    cursor,
    requestId,
    workspaceSlug,
    searchQuery,
  }: FetchWorkspaceSearchOptions,
  csrftoken?: string
) {
  const { filters, searchTerm } = buildSearchParams(workspaceSlug, searchQuery);
  return fetchSearchResults(
    { cursor, filters, requestId, searchTerm },
    csrftoken
  );
}

function buildSearchParams(workspaceSlug: string, searchQuery: string) {
  const { filter, terms } = parseQuery(searchQuery, {
    attributes: { deny: ['namespace'] },
    validationPolicy: 'strip',
  });

  const namespaceFilter = {
    attribute: 'namespace' as const,
    eq: workspaceSlug,
  };

  return {
    filters: filter
      ? composeScopedFilter(namespaceFilter, filter)
      : namespaceFilter,
    searchTerm: encodeQueryTerms(terms),
  };
}
